import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Grid, Table } from "@mantine/core"
import { useDebouncedState } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";

import {
  Button,
  EmptyMessage,
  Loader,
  Pagination,
  TextInput
} from "components";
import { AuthorityConstants, ServiceConstants } from "constant";
import { useListHook } from "hooks";
import { UserService } from "services";
import { ProfileResponse, UserRequest, UserResponse } from "types";
import { AppUtils, UserUtils } from "utils";

import { IRootState } from "config/redux";
import UsersListItem from "./item";
import UserForm from "../form";
import { TFunction } from "i18next";
import { tableClasses } from "styles";

const { ROLE_USR_C } = AuthorityConstants;
const { REQUEST_PENDING, REQUEST_USERS_LIST_ID, REQUEST_USERS_CREATE_ID } = ServiceConstants;

interface UsersListProps {
  profile?: ProfileResponse;
  profiles: ProfileResponse[]
}

const UsersList = ({ profile, profiles }: UsersListProps) => {
  const { t } = useTranslation();
  const serviceObject = useSelector((state: IRootState) => state.service.object[REQUEST_USERS_LIST_ID]);
  const { page, content, updatePageable, resetList, totalPages, refreshList, setPage } = useListHook();
  const [search, setSearch] = useDebouncedState('', 500);

  useEffect(() => {
    resetList();
  }, [resetList]);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, REQUEST_USERS_LIST_ID, updatePageable);
  }, [serviceObject, updatePageable]);

  const fetch = useCallback(() => UserService.usersAll({ page: page ?? 0, search: search, profileId: profile?.id, type: 'EMPLOYEE' }), [page, profile, search]);

  useEffect(() => {
    if (page === undefined) {
      return;
    }
    if (page === -1) {
      resetList();
      return;
    }
    fetch();
  }, [page, fetch, resetList]);

  useEffect(() => {
    if (search === undefined) {
      return;
    }
    refreshList();
  }, [search, refreshList]);
  const isLoading = useCallback(() => serviceObject && serviceObject.status === REQUEST_PENDING, [serviceObject]);

  const rows = useMemo(() => {
    if (!content) {
      return <></>;
    }
    return content.map((el: UserResponse) => <UsersListItem key={el.id} fetch={fetch} item={el} profiles={profiles} t={t} />
    )
  }, [content, fetch, profiles, t])

  return (
    <>
      <Grid pb={'md'}>
        <Grid.Col span={{ xs: 4, sm: 4, md: 6, lg: 8 }}>
          <UserNewModal
            fetch={fetch}
            profile={profile}
            profiles={profiles}
            t={t}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 8, sm: 8, md: 6, lg: 4 }}>
          <TextInput
            defaultValue={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder={t('user.list.search')}
          />
        </Grid.Col>
      </Grid>
      <Box pos={'relative'}>
        <Loader overlay={true} visible={content === undefined || isLoading()} />
        {content && (
          content.length === 0 ?
            <EmptyMessage message={t('user.list.empty')} />
          :
          <>
            <Table className={tableClasses.table}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('user.fullName.label')}</Table.Th>
                  <Table.Th visibleFrom={'md'}>{t('user.phone.label')}</Table.Th>
                  <Table.Th>{t('user.email.label')}</Table.Th>
                  <Table.Th>{t('user.status.label')}</Table.Th>
                  <Table.Th ta={'right'}>{t('actions')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{rows}</Table.Tbody>
            </Table>
            <Pagination value={page} onChange={setPage} total={totalPages} />
          </>
        )}
      </Box>
    </>
  )
}

interface NewModalProps extends UsersListProps {
  fetch: () => Promise<void>;
  t: TFunction;
}

const UserNewModal = ({ fetch, profile, profiles, t }: NewModalProps) => {
  const [opened, setOpened] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const serviceObject = useSelector((state: IRootState) => state.service.object[REQUEST_USERS_CREATE_ID]);

  useEffect(() => {
    if (!opened) setError(undefined)
  }, [opened]);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, REQUEST_USERS_CREATE_ID, async () => {
      await fetch();
      setOpened(false);
    },
    (err) => setError(err.data.message))
  }, [serviceObject, fetch]);

  const onSubmit = useCallback((data: UserRequest) => UserService.usersCreate(data), []);
  const isLoading = useMemo(() => serviceObject && serviceObject.status === REQUEST_PENDING, [serviceObject]);

  if (!UserUtils.hasAuthority(ROLE_USR_C)) {
    return <></>;
  }

  return (
    <>
      <Button size={'compact-lg'} onClick={() => setOpened(true)}>
        <IconPlus />
      </Button>
      <UserForm
        error={error}
        item={{
          countryCode: '+41',
          profile: profile
        } as UserRequest}
        loading={isLoading}
        opened={opened}
        onClose={() => setOpened(false)}
        onSubmit={onSubmit}
        profiles={profiles}
        t={t}
        title={t('user.modal.new.any')}
      />
    </>
  )
}


export default UsersList;
